/**
 * Iter 97.15 — Unified 3-mode rendering layout for the Commercials Builder.
 *
 * Operator directive (2026-02-27, confirmed message 475):
 *   "In Itemized all elements are displayed, only difference is price also
 *    displayed for each item. In Grouped, everything remains the same — just
 *    per-element prices omitted (only product price shown). In Consolidated,
 *    everything is same as Itemized — only prices are omitted for all
 *    elements and single consolidated price is shown."
 *
 * Materialisation contract:
 *   - INPUT  : `prototype` (rich Builder localStorage state)
 *              `products`  (aggregateScopeByProduct(prototype.scope))
 *   - OUTPUT : An array of `ProposalLineIn`-shaped objects to POST to
 *              /api/billing/proposal/{id}/commercials-builder/apply
 *
 * Emission rules — IDENTICAL across all 3 modes (only display-side rules
 * differ; the persisted truth is always the same):
 *
 *   • Products WITH modules:
 *       - 1 SYNTHETIC product_header line (amount=0, amc=0) carrying the
 *         product label + catalogue description. `option_label` = product
 *         label so the modules band together under their parent.
 *       - 1 module line per scope row (amount=setup, amc_amount=amc).
 *         `option_label` = product label so it joins the product band.
 *
 *   • Atomic products (no modules):
 *       - 1 atomic line per product (amount=setup, amc=amc).
 *         `option_label` = operator group label IF that product is assigned
 *         to a Builder group, otherwise "OTHER ITEMS" so all unassigned
 *         atomic products share a single "OPTION · OTHER ITEMS" band per
 *         operator spec.
 *
 *   • Every line carries `presentation_mode = mode` so the display layer
 *     (Proposal Detail / Print) can apply identical price-visibility rules
 *     to the Builder Preview even after the Builder localStorage is cleared.
 *
 * Display-side rules (enforced by PreviewPage / ProposalDetail / ProposalPrint):
 *   - Itemized     → all prices visible · product/group subtotals visible · grand total visible
 *   - Grouped      → module-row prices BLANK · subtotals visible · grand total visible
 *   - Consolidated → all row prices BLANK · subtotals HIDDEN · only grand total visible
 *
 * Blank cells are rendered as empty strings ("" — no dash, no zero) per
 * operator preference (2026-02-27, clarification message #1).
 *
 * Intentionally NOT materialised in this iteration (preserves blast radius):
 *   - Builder.discount → operator continues to use the Special Offer card.
 *   - Builder.deliveryModes → display-only chip strip in the builder preview.
 */
export function materializeBuilderToLineItems(prototype, products) {
    if (!prototype) return [];
    const m = prototype.commercials || {};
    const mode = m.presentationMode || "itemized";
    const gstShown = (prototype.taxDisplay?.mode || "show_gst") === "show_gst";
    const gstPercent = Number(prototype.taxDisplay?.gstPercent) || 18;

    /* AMC lookup keyed by scope row id (Builder stores AMC per scope row). */
    const amcByScopeRowId = {};
    (prototype.amc?.items || []).forEach((a) => {
        amcByScopeRowId[a.scopeRowId] = Number(a.amount) || 0;
    });

    /* Group lookup keyed by product key — first wins on the (rare) chance
       a product was assigned to multiple groups during legacy editing. */
    const groupByProductKey = {};
    (m.groups || []).forEach((g) => {
        (g.productKeys || []).forEach((k) => {
            if (!groupByProductKey[k]) groupByProductKey[k] = g;
        });
    });

    /* Default option band label for atomic standalones with no Builder group
       assignment — operator spec (2026-02-27): "OPTION · OTHER ITEMS". */
    const OTHER_ITEMS_LABEL = "OTHER ITEMS";

    /* Iter 97.16 — Builder.deliveryModes is keyed by product key. Snapshot
       the selection onto each product_header / atomic line so Proposal
       Detail + Print/PDF render the same icons + legend operator sees in
       the Builder Preview. `noDeliveryModes` products (e.g. Hosting) are
       always emitted with an empty array — no icon strip will render. */
    const deliveryModesByProductKey = m.deliveryModes || {};

    /* Annual Cost (2026-10) — an item carries Annual Cost INSTEAD of AMC
       when its scope row's recurring_kind is "annual_cost". */
    const isAnnualRow = (r) => r.recurring_kind === "annual_cost";
    const annualOf = (r) => (isAnnualRow(r) && r.annual_cost_pricing_mode !== "complimentary"
        ? (Number(m.annualCostPrices?.[r.id]) || 0) : 0);
    const annualFields = (rows, amcComplimentary) => {
        const out = amcComplimentary ? { amc_pricing_mode: "complimentary" } : {};
        const ac = rows.filter(isAnnualRow);
        if (ac.length === 0) return out;
        const comp = ac.every((r) => r.annual_cost_pricing_mode === "complimentary");
        return {
            ...out,
            annual_cost_amount: comp ? 0 : rows.reduce((s, r) => s + annualOf(r), 0),
            annual_cost_pricing_mode: comp ? "complimentary" : "priced",
        };
    };

    const lines = [];
    let hasAnyPrice = false;

    (products || []).forEach((p) => {
        const productDesc = (p.description || "").trim() || null;
        const productSetup = (p.scopeRows || []).reduce(
            (s, r) => s + (Number(m.itemPrices?.[r.id]) || 0),
            0,
        );
        const productAmc = (p.scopeRows || []).reduce(
            (s, r) => s + (Number(amcByScopeRowId[r.id]) || 0),
            0,
        );
        // Complimentary marker fix (2026-07-23) — treat a Setup/AMC
        // Complimentary marker on any scope row as commercial content
        // that must be materialised, even when both raw amounts are 0.
        // Before this fix the plain `productSetup <= 0 && productAmc <= 0`
        // gate dropped the ENTIRE product (synthetic product_header +
        // every module / atomic line) whenever every row was fully
        // Complimentary, which erased those items from the persisted
        // `line_items` and made them invisible on Proposal Detail /
        // Print / Preview-from-line_items. The Complimentary marker
        // is stored on `builder_state.scope[*].{setup,amc}_pricing_mode`
        // and the checkbox handler zeros the underlying itemPrices /
        // amc.items slot, so the raw sums are legitimately 0 in the
        // "all rows complimentary" case — the emission must not be
        // suppressed. Genuinely empty products (no price AND no
        // Complimentary marker) are still filtered as before.
        const hasAnyComplimentaryMarker = (p.scopeRows || []).some(
            (r) => r.setup_fee_pricing_mode === "complimentary"
                || r.amc_pricing_mode === "complimentary"
                || (isAnnualRow(r) && r.annual_cost_pricing_mode === "complimentary"),
        );
        const productAnnual = (p.scopeRows || []).reduce((s, r) => s + annualOf(r), 0);
        if (productSetup <= 0 && productAmc <= 0 && productAnnual <= 0 && !hasAnyComplimentaryMarker) return;
        hasAnyPrice = true;

        const group = groupByProductKey[p.key];
        const productDeliveryModes = p.noDeliveryModes
            ? []
            : (deliveryModesByProductKey[p.key] || []).map((c) => String(c));

        if (p.hasModules) {
            /* Multi-module product → option_label = product label so all
               its module rows + the synthetic header share an option band
               labelled "OPTION · <PRODUCT NAME>". If the operator has
               assigned this product to a Builder group, the group label
               wins (operator override). */
            const optionLabel = group ? (group.label || p.label) : p.label;

            // Synthetic product header — carries the product name + the
            // catalogue description + delivery-mode hints, but ZERO price.
            lines.push({
                product_name: p.label,
                product_type: "One-time",
                amount: 0,
                amc_amount: 0,
                billing_frequency: "Annual",
                gst_applicable: gstShown,
                gst_percent: gstPercent,
                hsn_sac_code: "997331",
                option_label: optionLabel,
                option_description: null,
                description: productDesc,
                scope_modules: [],
                row_kind: "product_header",
                presentation_mode: mode,
                delivery_modes: productDeliveryModes,
            });

            // One module line per scope row carrying the actual prices.
            (p.scopeRows || []).forEach((r) => {
                // Complimentary marker (2026-07-23) — belt-and-braces:
                // when a component is marked complimentary, force its
                // materialised amount to 0 even if the itemPrices /
                // amc.items maps still carry a stale value. The Builder
                // UI also zeroes and disables the input on toggle, but
                // this safety net guarantees the invariant
                //   `mode === "complimentary" ⇒ effective amount = 0`
                // regardless of future refactors.
                const setupComp = r.setup_fee_pricing_mode === "complimentary";
                const amcComp = !isAnnualRow(r) && r.amc_pricing_mode === "complimentary";
                const setup = setupComp ? 0 : (Number(m.itemPrices?.[r.id]) || 0);
                const amc = amcComp || isAnnualRow(r) ? 0 : (Number(amcByScopeRowId[r.id]) || 0);
                lines.push({
                    product_name: r.moduleLabel || r.productName || p.label,
                    product_type: "One-time",
                    amount: setup,
                    amc_amount: amc,
                    ...annualFields([r], amcComp),
                    billing_frequency: "Annual",
                    gst_applicable: gstShown,
                    gst_percent: gstPercent,
                    hsn_sac_code: "997331",
                    option_label: optionLabel,
                    option_description: null,
                    description: null,
                    scope_modules: [],
                    row_kind: "module",
                    presentation_mode: mode,
                    delivery_modes: [],
                });
            });
        } else {
            /* Atomic standalone → groups together with other unassigned
               atomics under "OTHER ITEMS" (operator spec); operator group
               label wins when explicitly assigned. */
            const optionLabel = group ? (group.label || OTHER_ITEMS_LABEL) : OTHER_ITEMS_LABEL;
            // For atomic products take the first scope row's price (the
            // scope rows for atomic products is a single synthetic row).
            // Complimentary marker (2026-07-23) — atomic products can
            // have Setup Complimentary + AMC Priced (and vice-versa),
            // so mask each row's per-component contribution when its
            // corresponding mode is "complimentary".
            const setup = (p.scopeRows || []).reduce(
                (s, r) => s + (r.setup_fee_pricing_mode === "complimentary"
                    ? 0
                    : (Number(m.itemPrices?.[r.id]) || 0)),
                0,
            );
            const amc = (p.scopeRows || []).reduce(
                (s, r) => s + (r.amc_pricing_mode === "complimentary" || isAnnualRow(r)
                    ? 0
                    : (Number(amcByScopeRowId[r.id]) || 0)),
                0,
            );
            lines.push({
                product_name: p.label,
                product_type: "One-time",
                amount: setup,
                amc_amount: amc,
                ...annualFields(p.scopeRows || [], (p.scopeRows || []).some((r) => !isAnnualRow(r) && r.amc_pricing_mode === "complimentary")),
                billing_frequency: "Annual",
                gst_applicable: gstShown,
                gst_percent: gstPercent,
                hsn_sac_code: "997331",
                option_label: optionLabel,
                option_description: null,
                description: productDesc,
                scope_modules: [],
                row_kind: "atomic",
                presentation_mode: mode,
                delivery_modes: productDeliveryModes,
            });
        }
    });

    /* Empty-state guard preserved — if no products have any price, return
       [] so handleApplyToProposal still surfaces "Add at least one product
       with a price". */
    if (!hasAnyPrice) return [];

    return lines;
}

/* Annual Cost rows that have neither an amount nor an explicit Complimentary
   marker — a missing amount must never be saved as a free item. */
export function annualCostGaps(prototype) {
    const prices = prototype?.commercials?.annualCostPrices || {};
    return (prototype?.scope || []).filter((r) => r.recurring_kind === "annual_cost"
        && r.annual_cost_pricing_mode !== "complimentary"
        && !(Number(prices[r.id]) > 0));
}
